<template>
  <PerfectScrollbar class="AllPwaMobile">
    <header v-if="isContractsPage" class="contracts-page-heading">
      <h2>{{ $t('investments.contractsTitle') }}</h2>
      <p>{{ $t('investments.contractsDescription') }}</p>
    </header>

    <div v-if="summary" class="investment-summary">
      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-icon">
            <VsxIcon iconName="DocumentText" :size="24" color="#BB86FC" />
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ summary.total_investments }}</div>
            <div class="summary-label">{{ $t('investments.totalInvestments') }}</div>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon">
            <VsxIcon iconName="Activity" :size="24" color="#4BC0C0" />
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ summary.active_investments }}</div>
            <div class="summary-label">{{ $t('investments.activeInvestments') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Investments List -->
    <div class="investments-list">
      <div v-if="!isContractsPage" class="filters-bar">
        <div class="filter-item">
          <label>{{ $t('investments.filters.status') }}</label>
          <select v-model="selectedStatus">
            <option value="all">{{ $t('investments.filters.all') }}</option>
            <option value="active">{{ $t('investments.status.active') }}</option>
            <option value="completed">{{ $t('investments.status.completed') }}</option>
            <option value="cancelled">{{ $t('investments.status.cancelled') }}</option>
          </select>
        </div>
        <div class="filter-item">
          <label>{{ $t('investments.filters.type') }}</label>
          <select v-model="selectedType">
            <option value="all">{{ $t('investments.filters.all') }}</option>
            <option value="investment">{{ $t('investments.types.investment') }}</option>
            <option value="depositing">{{ $t('investments.types.contract') }}</option>
          </select>
        </div>
      </div>
      <div v-if="investments.length === 0" class="no-investments">
        <VsxIcon iconName="DocumentText" :size="64" color="#666" />
        <h3>{{ isContractsPage ? $t('investments.noContracts') : $t('investments.noInvestments') }}</h3>
        <p>{{ isContractsPage ? $t('investments.noContractsDesc') : $t('investments.noInvestmentsDesc') }}</p>
        <router-link to="/investment" class="start-investing-btn">
          {{ $t('investments.startInvesting') }}
        </router-link>
      </div>

      <div v-else class="investments-container">
        <div
          v-for="investment in filteredInvestments"
          :key="investment.id"
          class="investment-card"
          :class="{ 'investment-completed': investment.status === 'completed' }"
        >
          <div class="investment-header">
            <div class="investment-title">
              <h4>{{ investment.plan_name }}</h4>
              <span class="plan-type" :class="'plan-type-' + investment.plan_type">
                {{ investment.plan_type === 'investment' ? $t('investments.types.investment') : $t('investments.types.contract') }}
              </span>
            </div>
            <div class="investment-status" :class="'status-' + investment.status">
              {{ getStatusText(investment.status) }}
            </div>
          </div>

          <div class="investment-details">
            <div v-if="isContractsPage" class="detail-row">
              <span class="detail-label">{{ $t('investments.contractNumber') }}</span>
              <span class="detail-value">INV-{{ investment.id }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('investments.principalAmount') }}</span>
              <span class="detail-value">{{ isContractsPage ? (investment.currency_name || investment.currency_code) : displayCurrencySymbol }} {{ formatCurrency(investment.principal_amount) }}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">{{ $t('investments.dailyInterest') }}</span>
              <span class="detail-value">{{ investment.interest_min_percent }}% - {{ investment.interest_max_percent }}%</span>
            </div>

            <div v-if="!isContractsPage" class="detail-row">
              <span class="detail-label">{{ $t('investments.totalPayouts') }}</span>
              <span class="detail-value">{{ displayCurrencySymbol }} {{ formatCurrency(investment.total_payouts) }}</span>
            </div>

            <div v-if="!isContractsPage" class="detail-row">
              <span class="detail-label">{{ $t('investments.pendingPayouts') }}</span>
              <span class="detail-value">{{ displayCurrencySymbol }} {{ formatCurrency(investment.pending_payouts) }}</span>
            </div>

            <div v-if="!isContractsPage" class="detail-row">
              <span class="detail-label">{{ $t('investments.netProfit') }}</span>
              <span class="detail-value" :class="{ 'profit-positive': investment.net_profit > 0, 'profit-negative': investment.net_profit < 0 }">
                {{ displayCurrencySymbol }} {{ formatCurrency(investment.net_profit) }}
              </span>
            </div>

            <div class="detail-row">
              <span class="detail-label">{{ $t('investments.startedAt') }}</span>
              <span class="detail-value">{{ formatDate(investment.started_at) }}</span>
            </div>

            <div v-if="!isContractsPage && investment.next_payout_at" class="detail-row">
              <span class="detail-label">{{ $t('investments.nextPayout') }}</span>
              <span class="detail-value">{{ formatDate(investment.next_payout_at) }}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">{{ $t('investments.endedAt') }}</span>
              <span class="detail-value">{{ formatDate(investment.ended_at) }}</span>
            </div>
          </div>

          <button
            v-if="isContractsPage"
            type="button"
            class="contract-download-button"
            :disabled="generatingContractId === investment.id"
            @click="downloadInvestmentContract(investment)"
          >
            <VsxIcon iconName="DocumentDownload" :size="20" color="currentColor" type="linear" />
            {{ generatingContractId === investment.id ? $t('investments.downloadingContract') : $t('investments.downloadContract') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="contractPreview" class="contract-pdf-preview-overlay" :dir="$i18n.locale === 'fa' ? 'rtl' : 'ltr'">
      <article
        id="registered-investment-contract"
        ref="contractContent"
        class="contract-pdf-document"
      >
        <h1>{{ $t('investment.contract.title') }}</h1>
        <p>{{ $t('investment.contract.intro', {
          startDate: formatDate(contractPreview.started_at),
          nationalId: contractPdfUser.nationalId,
          sex: contractPdfUser.sex,
          fullName: contractPdfUser.fullName,
          nationalCode: contractPdfUser.nationalCode,
        }) }}</p>
        <p>{{ $t('investment.contract.contractNumber', { number: `INV-${contractPreview.id}` }) }}</p>

        <h2>{{ $t('investment.contract.article1.title') }}</h2>
        <p>{{ $t('investment.contract.article1.content', {
          amount: formatCurrency(contractPreview.principal_amount),
          currency: contractPreview.currency_name || contractPreview.currency_code,
        }) }}</p>

        <h2>{{ $t('investment.contract.article2.title') }}</h2>
        <p>{{ $t('investment.contract.article2.content', {
          startDate: formatDate(contractPreview.started_at),
          endDate: formatDate(contractPreview.ended_at),
        }) }}</p>

        <h2>{{ $t('investment.contract.article3.title') }}</h2>
        <p>{{ $t('investment.contract.article3.content', {
          minInterest: contractPreview.interest_min_percent,
          maxInterest: contractPreview.interest_max_percent,
        }) }}</p>

        <h2>{{ $t('investment.contract.article4.title') }}</h2>
        <p>{{ $t('investment.contract.article4.content') }}</p>
        <h2>{{ $t('investment.contract.article5.title') }}</h2>
        <p>{{ $t('investment.contract.article5.content') }}</p>
        <h2 class="contract-company-name">{{ $t('investment.contract.companyName') }}</h2>
        <img
          class="contract-pdf-signature"
          src="/img/signature.jpg"
          :alt="$t('investment.contract.signatureAlt')"
          @load="contractSignatureLoaded = true"
          @error="contractSignatureLoaded = false"
        />
        <p>{{ contractPdfUser.fullName }} — {{ $t('investments.investor') }}</p>
      </article>
    </div>
  </PerfectScrollbar>
</template>

<script>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import axios from "@/axios"
import { toast } from "vue3-toastify"
import moment from 'moment-jalaali'
import html2pdf from 'html2pdf.js'

export default {
  name: 'myInvestments',
  components: {
    PerfectScrollbar
  },
  data() {
    return {
      color: localStorage.getItem('theme') === 'dark' ? '#BB86FC' : '#2E1977',
      investments: [],
      summary: null,
      displayCurrencyCode: 'USD',
      displayCurrencySymbol: '$',
      selectedStatus: 'all',
      selectedType: 'all',
      generatingContractId: null,
      contractPreview: null,
      contractPdfUser: { fullName: '', nationalId: '', nationalCode: '', sex: '' },
      contractSignatureLoaded: false
    }
  },
  computed: {
    isContractsPage() {
      return this.$route.path === '/contracts'
    },
    filteredInvestments() {
      let list = this.investments || []
      if (this.isContractsPage) return list
      if (this.selectedStatus !== 'all') {
        list = list.filter(inv => inv.status === this.selectedStatus)
      }
      if (this.selectedType !== 'all') {
        list = list.filter(inv => inv.plan_type === this.selectedType)
      }
      return list
    }
  },
  methods: {
    async fetchInvestments() {
      const userData = localStorage.getItem('user')
      if (!userData) {
        this.$router.push('/login')
        return
      }

      try {
        this.userLis = JSON.parse(userData)
      } catch (error) {
        console.error('Error parsing user data:', error)
        this.$router.push('/login')
        return
      }

      let attempts = 0
      const maxAttempts = 3

      while (attempts < maxAttempts) {
        try {
          const response = await axios.post('/get-user-investments', {
            id: this.userLis.id
          })

          if (response.data && response.data.success) {
            this.investments = response.data.data.investments || []
            this.summary = response.data.data.summary || null

            // Update display currency based on investments
            this.updateDisplayCurrencyFromInvestments()

            return
          } else {
            throw new Error('Invalid response')
          }

        } catch (error) {
          console.error(`Attempt ${attempts + 1} failed:`, error)

          if (attempts === maxAttempts - 1) {
            toast(this.$t('errors.fetchFailed'), {
              autoClose: 3000,
              position: "top-right",
              type: "error",
            })
          }
        }

        attempts++
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
    },

    updateDisplayCurrencyFromInvestments() {
      if (this.investments.length > 0) {
        // Use the most common currency (by name when available)
        const names = this.investments.map(inv => inv.currency_name || inv.currency_code)
        const mostCommonName = names.reduce((a, b, i, arr) =>
          arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b
        )

        const matched = this.investments.find(inv => (inv.currency_name || inv.currency_code) === mostCommonName)
        this.displayCurrencyCode = (matched && matched.currency_code) || 'USD'
        this.displayCurrencySymbol = mostCommonName || this.displayCurrencyCode
        return
      }

      // Fallback when there is no investment yet
      this.displayCurrencySymbol = this.displayCurrencyCode
    },

    formatCurrency(amount) {
      const numericAmount = parseFloat(amount)
      if (isNaN(numericAmount)) {
        return '0.00'
      }
      return numericAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = moment(dateString)
      return this.$i18n.locale === 'fa' ? date.format('jYYYY/jMM/jDD') : date.format('YYYY/MM/DD')
    },

    getStatusText(status) {
      const statusMap = {
        'active': this.$t('investments.status.active'),
        'completed': this.$t('investments.status.completed'),
        'cancelled': this.$t('investments.status.cancelled')
      }
      return statusMap[status] || status
    },

    getContractUser() {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        return {
          fullName: user.full_name || user.name || [user.firstname, user.lastname].filter(Boolean).join(' '),
          nationalId: user.nationalId || user.national_id || '',
          nationalCode: user.nationalcode || user.national_code || user.nationalCode || '',
          sex: user.sex || '',
        }
      } catch (error) {
        return { fullName: '', nationalId: '', nationalCode: '', sex: '' }
      }
    },

    async downloadInvestmentContract(investment) {
      if (this.generatingContractId !== null) return
      this.generatingContractId = investment.id

      try {
        this.contractPreview = investment
        this.contractPdfUser = this.getContractUser()
        this.contractSignatureLoaded = false
        await this.$nextTick()
        const signature = this.$refs.contractContent?.querySelector('.contract-pdf-signature')
        if (signature?.complete && signature.naturalWidth > 0) {
          this.contractSignatureLoaded = true
        } else if (signature) {
          await new Promise((resolve, reject) => {
            signature.addEventListener('load', () => {
              this.contractSignatureLoaded = true
              resolve()
            }, { once: true })
            signature.addEventListener('error', reject, { once: true })
          })
        }
        if (!signature || !this.contractSignatureLoaded || !signature.naturalWidth) {
          throw new Error('Contract signature image could not be loaded')
        }

        const signatureCanvas = document.createElement('canvas')
        signatureCanvas.width = signature.naturalWidth
        signatureCanvas.height = signature.naturalHeight
        const signatureContext = signatureCanvas.getContext('2d')
        if (!signatureContext) throw new Error('Contract signature canvas is unavailable')
        signatureContext.drawImage(signature, 0, 0)
        const embeddedSignature = signatureCanvas.toDataURL('image/png')
        signature.src = embeddedSignature
        if (typeof signature.decode === 'function') await signature.decode()

        if (document.fonts?.ready) await document.fonts.ready
        await document.fonts.load('15px iranyekan')

        const contractDocument = this.$refs.contractContent
        const contractBounds = contractDocument?.getBoundingClientRect()
        if (!contractDocument || !contractDocument.textContent.trim() || contractBounds.width <= 0 || contractBounds.height <= 0) {
          throw new Error('Contract document has no renderable content')
        }

        const pdfWorker = html2pdf()
          .set({
            margin: [12, 12, 12, 12],
            filename: `investment-contract-INV-${investment.id}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              foreignObjectRendering: true,
              backgroundColor: '#ffffff',
              windowWidth: 1024,
              onclone: clonedDocument => {
                const clonedContract = clonedDocument.getElementById('registered-investment-contract')
                if (!clonedContract) return
                clonedContract.style.position = 'static'
                clonedContract.style.left = 'auto'
                clonedContract.style.top = 'auto'
                clonedContract.style.zIndex = 'auto'
                clonedContract.style.opacity = '1'
                clonedContract.style.visibility = 'visible'
                clonedContract.style.display = 'block'
                clonedContract.style.transform = 'none'
                clonedContract.style.width = '794px'
                clonedContract.style.height = 'auto'
                clonedContract.style.maxHeight = 'none'
                clonedContract.style.overflow = 'visible'
                clonedContract.style.fontFamily = "'iranyekan', Tahoma, sans-serif"
                clonedContract.style.fontKerning = 'normal'
                clonedContract.style.fontVariantLigatures = 'normal'
                clonedContract.style.letterSpacing = 'normal'
                clonedContract.style.wordSpacing = 'normal'
                clonedContract.style.direction = clonedContract.getAttribute('dir') || 'ltr'
                const clonedSignature = clonedContract.querySelector('.contract-pdf-signature')
                if (clonedSignature) {
                  clonedSignature.src = embeddedSignature
                  clonedSignature.style.display = 'block'
                  clonedSignature.style.visibility = 'visible'
                  clonedSignature.style.opacity = '1'
                }
                let parent = clonedContract.parentElement
                while (parent && parent !== clonedDocument.body) {
                  parent.style.position = 'static'
                  parent.style.width = '794px'
                  parent.style.height = 'auto'
                  parent.style.maxHeight = 'none'
                  parent.style.overflow = 'visible'
                  parent.style.transform = 'none'
                  parent.style.background = '#ffffff'
                  parent = parent.parentElement
                }
              },
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          })
          .from(contractDocument)
          .toCanvas()

        await pdfWorker.toPdf().save()
      } catch (error) {
        console.error('Error generating investment contract PDF:', error)
        toast(this.$t('investment.downloadContractFailed'), {
          autoClose: 3000,
          position: 'top-right',
          type: 'error',
        })
      } finally {
        this.contractPreview = null
        this.generatingContractId = null
      }
    },

    loadTheme() {
      const savedTheme = localStorage.getItem('theme')
      this.color = savedTheme === "light" ? '#2E1977' : '#BB86FC'
    }
  },

  mounted() {
    this.fetchInvestments()
    setInterval(() => {
      this.loadTheme()
    }, 100)
  }
}
</script>

<style scoped>
/* Minimal Investment Page Styles */

.contracts-page-heading {
  margin: 28px 20px 0;
  padding: 0 4px;
}

.contracts-page-heading h2 {
  margin: 0 0 8px;
  color: #212529;
  font-size: 1.55rem;
  font-weight: 700;
}

.contracts-page-heading p {
  max-width: 68ch;
  margin: 0;
  color: #5f6670;
  line-height: 1.7;
}

.contract-download-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  margin-top: 18px;
  padding: 10px 16px;
  border: 1px solid #3d268e;
  border-radius: 9px;
  background: #3d268e;
  color: #fff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.contract-download-button:hover:not(:disabled) {
  border-color: #2e1977;
  background: #2e1977;
  transform: translateY(-1px);
}

.contract-download-button:focus-visible {
  outline: 3px solid #bb86fc;
  outline-offset: 3px;
}

.contract-download-button:disabled {
  cursor: wait;
  opacity: 0.7;
}

.contract-pdf-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  justify-content: center;
  overflow: auto;
  padding: 24px 12px;
  background: rgba(15, 12, 22, 0.72);
}

.contract-pdf-document {
  box-sizing: border-box;
  width: min(794px, 100%);
  min-height: 1123px;
  height: max-content;
  margin: 0 auto;
  padding: 42px;
  background: #fff;
  color: #20202a;
  font-family: 'iranyekan', Tahoma, sans-serif;
  font-size: 15px;
  line-height: 1.9;
  text-align: start;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
}

.contract-pdf-document h1 {
  margin: 0 0 24px;
  font-size: 22px;
  text-align: center;
}

.contract-pdf-document h2 {
  margin: 18px 0 5px;
  font-size: 17px;
}

.contract-pdf-document p {
  margin: 0 0 12px;
}

.contract-pdf-document .contract-company-name {
  margin-top: 28px;
  font-size: 16px;
}

.contract-pdf-signature {
  display: block;
  width: 180px;
  max-width: 100%;
  height: auto;
  margin: 8px 0 12px;
  object-fit: contain;
}

.investment-summary {
  margin: 30px 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  background: #fff;
  border-radius: 15px;
  padding: 10px 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-color: #dee2e6;
}

.summary-icon {
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
}

.summary-content {
  flex: 1;
}

.summary-value {
  font-size: 1.5rem;
  
  color: #495057;
}

.summary-label {
  font-size: 1rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Investments List */
.investments-list {
  padding: 0 20px 40px;
}

.filters-bar {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin: 0 0 16px 0;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-item label {
  font-size: 0.9rem;
  color: #6c757d;
}

.filter-item select {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px 10px;
  color: #212529;
}

.no-investments {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 15px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.no-investments svg {
  color: #adb5bd;
  margin-bottom: 20px;
}

.no-investments h3 {
  margin: 0 0 15px;
  color: #212529;
  font-size: 1.8rem;
}

.no-investments p {
  margin-bottom: 30px;
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.6;
}

.start-investing-btn {
  display: inline-block;
  background: linear-gradient(135deg, #28a745, #1e7e34);
  color: #fff;
  padding: 15px 35px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(40, 167, 69, 0.3);
  border: none;
}

.start-investing-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(40, 167, 69, 0.4);
  background: linear-gradient(135deg, #1e7e34, #155724);
}

/* Investment Cards */
.investments-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.investment-card {
  background: #fff;
  border-radius: 15px;
  padding: 25px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.investment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-color: #dee2e6;
}

.investment-completed {
  background: #f8fff9;
  border-color: #d1ecf1;
}

.investment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.investment-title h4 {
  margin: 0 0 8px 0;
  color: #212529;
  font-size: 1.3rem;
}

.plan-type {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.plan-type-investment {
  background: #e7f3ff;
  color: #007bff;
}

.plan-type-depositing {
  background: #f8d7da;
  color: #dc3545;
}

.investment-status {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: #d1ecf1;
  color: #0c5460;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-cancelled {
  background: #f8d7da;
  color: #721c24;
}

.investment-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f8f9fa;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 1rem;
  
  color: #6c757d;
}

.detail-value {
  font-size: 1rem;
  color: #212529;
}

.profit-positive {
  color: #28a745 !important;
}

.profit-negative {
  color: #dc3545 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .AllPwaMobile {
    padding-bottom: 100px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    margin: 20px 10px;
  }

  .summary-card {
    padding: 20px;
    gap: 15px;
  }

  .summary-value {
    font-size: 1.6rem;
  }

  .summary-label {
    font-size: 0.9rem;
  }

  .investments-list {
    padding: 0 10px 30px;
  }
  .contracts-page-heading {
    margin: 20px 14px 0;
  }
  .filters-bar { margin-bottom: 12px; }
  .filter-item select { padding: 8px; }

  .investment-card {
    padding: 20px;
    margin: 0 5px;
  }

  .investment-header {
    gap: 10px;
  }

  .investment-title h4 {
    font-size: 1.1rem;
  }

  .detail-label,
  .detail-value {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .summary-card {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .summary-icon {
    width: 50px;
    height: 50px;
  }

  .investment-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .investment-status {
    align-self: flex-end;
  }
}

</style>
